import {
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  GridApi,
  GridColDef,
  GridColumnVisibilityModel,
  GridPinnedColumnFields,
} from '@mui/x-data-grid-premium';

import { HeaderRequest, HeaderResponse } from '@/api/data-contracts';

export const INTERNAL_FIELDS = new Set([
  '__check__',
  '__row_group_by_columns_group__',
  'serviceTypeIcon',
]);
export const ACTIONS_FIELD = 'actions';

// Columns MUI injects (tree data, detail panels): never part of the saved order. The
// detail-panel toggle is in getAllColumns() even while a page hides it (OrderBookings).
export const NON_PERSISTED_FIELDS = new Set([
  ...INTERNAL_FIELDS,
  '__tree_data_group__',
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
]);

// Columns the grid or the page decides on its own (injected, system- or page-pinned):
// never persisted as visible or pinned, and skipped on restore.
export const GRID_MANAGED_FIELDS = new Set([...NON_PERSISTED_FIELDS, ACTIONS_FIELD]);

const sanitizePinnedList = (fields: string[] | undefined): string[] =>
  (fields ?? []).filter((f) => !GRID_MANAGED_FIELDS.has(f));

const normalizePinned = (
  pinned: { left?: string[]; right?: string[] } | null | undefined,
): { left: string[]; right: string[] } | null => {
  if (!pinned) return null;
  const left = sanitizePinnedList(pinned.left);
  const right = sanitizePinnedList(pinned.right);
  return left.length === 0 && right.length === 0 ? null : { left, right };
};

// Column defs on the persisted-width path carry their natural shrink bound so
// the resize interaction can clamp against it instead of the persisted width.
export interface ResizableGridColDef extends GridColDef {
  generatikResizeFloor?: number;
}

const FIELD_TO_BACKEND: Record<string, string> = { serviceTypeIcon: 'serviceType' };
const BACKEND_TO_FIELD: Record<string, string> = { serviceType: 'serviceTypeIcon' };

export const toBackendField = (field: string): string => FIELD_TO_BACKEND[field] ?? field;
export const fromBackendField = (field: string): string => BACKEND_TO_FIELD[field] ?? field;

export const pinActionsColumn = <T extends { field: string; disableReorder?: boolean }>(
  columns: T[],
): T[] => {
  const actionsIndex = columns.findIndex((col) => col.field === ACTIONS_FIELD);
  if (actionsIndex < 0) return columns;
  const result = [...columns];
  const [actionsCol] = result.splice(actionsIndex, 1);
  result.unshift({ ...actionsCol, disableReorder: true });
  return result;
};

export const enforceFixedOrder = (fields: string[]): string[] => {
  const actionsIdx = fields.indexOf(ACTIONS_FIELD);
  if (actionsIdx <= 0) return fields;
  const result = [...fields];
  result.splice(actionsIdx, 1);
  result.unshift(ACTIONS_FIELD);
  return result;
};

export const extractColumnOrderFromApi = (
  apiRef: { current: GridApi | null | undefined },
  fallbackColumns: GridColDef[],
): string[] => {
  let order: string[];
  try {
    if (!apiRef.current) throw new Error('apiRef not ready');
    order = apiRef.current
      .getAllColumns()
      .map((col) => col.field)
      .filter((field) => !NON_PERSISTED_FIELDS.has(field));
  } catch {
    order = fallbackColumns.map((col) => col.field);
  }
  return enforceFixedOrder(order);
};

interface BuildHeaderRequestParams {
  pageType: string;
  resourceId?: string;
  order: string[];
  processedColumns: GridColDef[];
  effectiveColumnVisibilityModel: Record<string, boolean>;
  groupByField?: string | null;
  pinnedColumns?: GridPinnedColumnFields;
}

export const buildHeaderRequest = ({
  pageType,
  resourceId,
  order,
  processedColumns,
  effectiveColumnVisibilityModel,
  groupByField,
  pinnedColumns,
}: BuildHeaderRequestParams): HeaderRequest => {
  const allFields = new Set(processedColumns.map((col) => col.field));
  const visibleColumns = order.filter(
    (field) =>
      allFields.has(field) &&
      !GRID_MANAGED_FIELDS.has(field) &&
      effectiveColumnVisibilityModel[field] !== false,
  );
  // Explicit empty lists, not normalizePinned's null: unpinning everything must clear the
  // saved pins however the backend reads null. The fingerprint treats the two as equal.
  const sanitizedPinned = pinnedColumns
    ? {
        left: sanitizePinnedList(pinnedColumns.left),
        right: sanitizePinnedList(pinnedColumns.right),
      }
    : null;
  return {
    pageType: pageType as HeaderRequest['pageType'],
    resourceId: resourceId ?? '',
    order,
    visibleColumns,
    extraDetails: {
      groupByField: groupByField ? toBackendField(groupByField) : null,
      pinnedColumns: sanitizedPinned,
    } as unknown as HeaderRequest['extraDetails'],
  };
};

interface HeaderExtraDetails {
  groupByField?: string | null;
  pinnedColumns?: { left?: string[]; right?: string[] } | null;
}

/**
 * Stable content fingerprint of a header request, for change detection.
 *
 * The /headers row is ONE record per (user, pageType) and `updateHeader`
 * replaces it wholesale, so a save that carries the same content as the last
 * synced state is not a harmless no-op: it can overwrite a NEWER row written
 * by another session/tab between our read and our debounced write
 * (read-modify-write, last-writer-wins). Comparing fingerprints lets the sync
 * hook skip those writes entirely — only genuine changes reach the backend.
 */
export const canonicalizeHeaderRequest = (request: HeaderRequest): string => {
  const extra = (request.extraDetails ?? {}) as HeaderExtraDetails;
  return JSON.stringify({
    pageType: request.pageType,
    resourceId: request.resourceId ?? '',
    order: request.order ?? [],
    visibleColumns: request.visibleColumns ?? [],
    groupByField: extra.groupByField ?? null,
    pinnedColumns: normalizePinned(extra.pinnedColumns),
  });
};

export const buildPinnedColumnsFromResponse = (
  response: HeaderResponse,
): GridPinnedColumnFields | null =>
  normalizePinned(
    response.extraDetails?.pinnedColumns as GridPinnedColumnFields | null | undefined,
  );

export const buildVisibilityModelFromResponse = (
  response: HeaderResponse,
  processedColumns: GridColDef[],
): GridColumnVisibilityModel | null => {
  if (!response.visibleColumns?.length) return null;
  const visibleSet = new Set(response.visibleColumns);
  const savedFields = response.order ?? [];
  const savedFieldSet = new Set(savedFields);
  const model: GridColumnVisibilityModel = {};
  const fields = [...savedFields, ...processedColumns.map((col) => col.field)];
  for (const field of fields) {
    if (GRID_MANAGED_FIELDS.has(field)) continue;
    const isNewColumn = !savedFieldSet.has(field);
    model[field] = isNewColumn || visibleSet.has(field);
  }
  return model;
};

export const applyOrder = (columns: GridColDef[], order: string[] | null): GridColDef[] => {
  if (!order?.length) return columns;
  const map = new Map(columns.map((col) => [col.field, col]));
  const result: GridColDef[] = [];
  for (const field of order) {
    const col = map.get(field);
    if (col) {
      result.push(col);
      map.delete(field);
    }
  }
  for (const col of map.values()) {
    result.push(col);
  }
  return result;
};

/**
 * The loaded row re-expressed as the request this grid would send for it — the
 * baseline `canonicalizeHeaderRequest` comparisons start from. It is projected onto
 * the grid's current columns, so a row that lists columns this grid doesn't render
 * (another page sharing the pageType, permission-gated columns not loaded yet) still
 * reads as unchanged. Any other mismatch (schema drift, internal fields in an old row)
 * only makes the first save look like a change, never loses a user change.
 */
export const projectResponseAsRequest = ({
  pageType,
  resourceId,
  response,
  processedColumns,
}: {
  pageType: string;
  resourceId?: string;
  response: HeaderResponse;
  processedColumns: GridColDef[];
}): HeaderRequest => {
  const order = enforceFixedOrder(
    applyOrder(processedColumns, response.order ?? null)
      .map((col) => col.field)
      .filter((field) => !NON_PERSISTED_FIELDS.has(field)),
  );
  const allFields = new Set(processedColumns.map((col) => col.field));
  const visibilityModel = buildVisibilityModelFromResponse(response, processedColumns);
  const visibleColumns = order.filter(
    (field) =>
      allFields.has(field) &&
      !GRID_MANAGED_FIELDS.has(field) &&
      (visibilityModel ? visibilityModel[field] !== false : true),
  );
  const extra = (response.extraDetails ?? {}) as HeaderExtraDetails;
  return {
    pageType: pageType as HeaderRequest['pageType'],
    resourceId: resourceId ?? '',
    order,
    visibleColumns,
    extraDetails: {
      groupByField: extra.groupByField ?? null,
      pinnedColumns: normalizePinned(extra.pinnedColumns),
    } as unknown as HeaderRequest['extraDetails'],
  };
};

export const applyStoredWidths = (
  columns: GridColDef[],
  storedWidths: Record<string, number> | undefined,
): GridColDef[] => {
  if (!storedWidths) return columns;
  return columns.map((col) => {
    if (col.resizable === false && col.minWidth != null && col.minWidth === col.maxWidth)
      return col;
    const persistedWidth = storedWidths[col.field];
    if (persistedWidth == null) return col;
    // The persisted width acts as a floor and a px-scale flex ratio, not a
    // hard width: columns keep the user's proportions but still stretch to
    // cover the grid's full width when there is spare room (e.g. after hiding
    // columns). The minWidth must stay at the persisted value — when the grid
    // overflows, MUI renders every flex column AT its minWidth, so this is
    // what makes persisted widths exact on scrolling tables. That same
    // minWidth would freeze resizing (the resize clamp reads colDef.minWidth
    // at drag start and the rendered width never sits below the persisted
    // one), so the column's natural shrink bound is stashed alongside and the
    // grid's onColumnResizeStart lowers the clamp to it for the drag.
    // The column's own minWidth (the header-label floor) also still applies:
    // widths persisted before that floor grew must not squeeze the chrome.
    const flexBasis =
      col.maxWidth != null ? Math.min(persistedWidth, col.maxWidth) : persistedWidth;
    const minWidth =
      col.maxWidth != null
        ? Math.min(Math.max(flexBasis, col.minWidth ?? 0), col.maxWidth)
        : Math.max(flexBasis, col.minWidth ?? 0);
    const resizeFloor =
      col.minWidth != null && col.maxWidth != null
        ? Math.min(col.minWidth, col.maxWidth)
        : col.minWidth;
    const next: ResizableGridColDef = {
      ...col,
      width: undefined,
      flex: flexBasis,
      minWidth,
      generatikResizeFloor: resizeFloor,
    };
    return next;
  });
};
